// ----------------------------------------------------------------------------
// Client-side SHA-256 hashing for Google Ads Enhanced Conversions and any
// other vendor that wants hashed PII (so it never leaves the browser raw).
//
// Uses the Web Crypto API (built into every modern browser; no dependency).
// Normalizes inputs per Google's requirements:
//   - email: trim, lowercase
//   - phone: E.164 (strip non-digits, ensure leading +)
// ----------------------------------------------------------------------------

const toHex = (buffer: ArrayBuffer): string =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

/**
 * SHA-256 of the input string. Returns lowercase hex. Returns empty string
 * if Web Crypto isn't available (very old browsers / non-secure contexts).
 */
export const sha256Hex = async (value: string): Promise<string> => {
  if (typeof crypto === "undefined" || !crypto.subtle) return "";
  const data = new TextEncoder().encode(value);
  const buffer = await crypto.subtle.digest("SHA-256", data);
  return toHex(buffer);
};

/** Normalize + hash an email per Google's Enhanced Conversions spec. */
export const hashEmail = async (email: string): Promise<string> => {
  const normalized = (email || "").trim().toLowerCase();
  if (!normalized) return "";
  return sha256Hex(normalized);
};

/** Normalize phone to E.164 (digits-only, leading +) and hash it. */
export const hashPhone = async (phone: string): Promise<string> => {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  // If the original already looked like E.164, keep its +. Otherwise the
  // user's TPhoneInput already produces a digits-only string with country
  // code prefix, so we just prefix +.
  const normalized = `+${digits}`;
  return sha256Hex(normalized);
};
